/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { t } from '@sportninja/common/i18n'
import React from 'react'
import { useGameRoster } from '../hooks/useGameRoster'
import { useOfficialColumns } from '../hooks/useOfficialColumns'
import { usePlayerColumns } from '../hooks/usePlayerColumns'
import type {
  GameRosterOfficial,
  GameRosterPlayer,
  RosterTypeOption,
} from '../types'
import { sortByName } from '../utils/rosterSort'
import { splitRosterPlayers } from '../utils/splitRosterPlayers'
import GameRosterError from './GameRosterError'
import GameRosterSkeleton from './GameRosterSkeleton'
import RosterSection from './RosterSection'

export interface GameRosterContentProps {
  gameId: string
  rosterId: string
  isGameStarted: boolean
  isGameCompleted: boolean
  isGenericGame: boolean
  // Soccer game: goalies are keepers.
  isSoccer: boolean
  canViewAttendanceInfo: boolean
  isInFrame: boolean
  playerTypes: RosterTypeOption[]
  teamOfficialTypes: RosterTypeOption[]
}

/** The roster's Players, Spares and Team Officials tables. */
const GameRosterContent: React.FC<GameRosterContentProps> = ({
  gameId,
  rosterId,
  isGameStarted,
  isGameCompleted,
  isGenericGame,
  isSoccer,
  canViewAttendanceInfo,
  isInFrame,
  playerTypes,
  teamOfficialTypes,
}) => {
  const { players, officials, isLoading, error } = useGameRoster(
    gameId,
    rosterId
  )
  const statusContext = {
    canViewAttendanceInfo,
    isGameStarted,
    isGameCompleted,
  }
  const playerColumns = usePlayerColumns({
    playerTypes,
    isInFrame,
    isSpares: false,
    isSoccer,
    ...statusContext,
  })
  const spareColumns = usePlayerColumns({
    playerTypes,
    isInFrame,
    isSpares: true,
    isSoccer,
    ...statusContext,
  })
  const officialColumns = useOfficialColumns({
    teamOfficialTypes,
    canViewAttendanceInfo,
    isGenericGame,
    isGameCompleted,
  })

  if (isLoading) {
    return <GameRosterSkeleton />
  }
  if (error) {
    return <GameRosterError message={error} />
  }

  const { regulars, spares } = splitRosterPlayers(players)

  return (
    <div css={sectionsStyle}>
      <RosterSection<GameRosterPlayer>
        title='Players'
        rows={sortByName(regulars)}
        columnDefs={playerColumns}
        emptyMessage='No Players Found'
        data-testid='game-roster-players'
      />
      {spares.length > 0 ? (
        <RosterSection<GameRosterPlayer>
          title='Spares'
          rows={sortByName(spares)}
          columnDefs={spareColumns}
          emptyMessage={t('common:affiliate_plural', { count: 2 })}
          data-testid='game-roster-spares'
        />
      ) : null}
      {officials.length > 0 ? (
        <div css={officialsStyle}>
          <RosterSection<GameRosterOfficial>
            title='Team Officials'
            rows={sortByName(officials)}
            columnDefs={officialColumns}
            emptyMessage={t('common:official_plural', { count: 2 })}
            data-testid='game-roster-officials'
          />
        </div>
      ) : null}
    </div>
  )
}

export default GameRosterContent

// ---- styled ----
const sectionsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
`
// The divider that sets the officials apart from the players.
const officialsStyle = css`
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`
